// ─────────────────────────────────────────────
//  DirectivesCard — what the board has asked of YOU, on the web.
//
//  ⚠️ WHY THIS EXISTS. The reply controls lived ONLY in `MobileTasksCard`, and
//  that renders only inside the native shell (`MobileShellGate` branches on
//  `isNative()`). So a clinician using ATC in a browser — which is most of them,
//  and every one of them before TestFlight ships — could be sent to cover a
//  break and had no way to accept it or refuse it. With the unanswered-directive
//  escalation now live, the board would go on nagging about a person who had no
//  control to press.
//
//  ⚠️ THE ANSWER LOGIC IS NOT COPIED. `useDirectiveReply` owns the writes and
//  their rollbacks for both surfaces; this file is presentation only. Copying
//  it is how BreakEnginePanel and VoiceControl drifted from the break card and
//  made "synced everywhere" untrue.
//
//  ⚠️ ABSENT WHEN EMPTY, BECAUSE THIS SITS IN THE LOBBY. HomePage is a boot
//  path whose own rule is that nothing on it may gate the page or break when a
//  hook returns nothing. A card announcing "no directives" is noise on the
//  screen a clinician crosses twice a day; a card that is simply not there is
//  not. It renders nothing at all until something is actually being asked.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useDayKey } from "../hooks/useDayKey";
import { useTasks } from "../hooks/useTasks";
import useDirectiveReply from "../hooks/useDirectiveReply";
import {
  tasksForRecipient,
  requiresConfirm,
  answerOf,
  declineOf,
} from "../domain/tasks";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;
const { lobby } = color;

// ⚠️ PRESET REASONS, NOT A TEXT BOX — the same four the phone offers, and for
// the same reason: this is answered by somebody who may be scrubbed, holding an
// airway, or halfway through an induction. A keyboard at that moment means the
// refusal never gets sent, and silence is what this control exists to replace.
// Shared verbatim with MobileTasksCard so the board reads one vocabulary.
export const DECLINE_REASONS = [
  "In a case — can't leave",
  "Induction or emergence",
  "Already covering another room",
  "Off shift / going home",
];

const clockText = (stamp) => {
  if (!stamp) return "";
  const d = typeof stamp?.toDate === "function" ? stamp.toDate() : stamp;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

function DirectiveRow({ task, uid, reply }) {
  const [picking, setPicking] = useState(false);
  const asks = requiresConfirm(task);
  const answer = asks ? answerOf(task, uid) : null;
  const refusal = declineOf(task, uid);
  const declined = answer === "declined";
  const seenLocally = Boolean(reply.seenLocally[task.id]);
  const answered = asks && !declined && (seenLocally || answer === "confirmed");
  const busy = reply.busyId === task.id;
  const failed = Boolean(reply.failedIds[task.id]);

  return (
    <li style={s.row}>
      <div style={s.meta}>
        <span style={s.from}>{task.senderName || "The board"}</span>
        {task.roomName ? <span style={s.room}>{task.roomName}</span> : null}
        <span style={s.time}>{clockText(task.createdAt)}</span>
      </div>

      <p style={s.text}>{task.text}</p>

      {!asks ? null : declined ? (
        <p style={s.declined} role="status">
          You said no — {refusal?.reason || "no reason recorded"}
        </p>
      ) : answered ? (
        <p style={s.seen}>seen {clockText(task?.confirmations?.[uid]) || "just now"}</p>
      ) : picking ? (
        <div style={s.why} role="group" aria-label="Why can you not come?">
          <p style={s.whyTitle}>Why not?</p>
          {DECLINE_REASONS.map((reason) => (
            <button
              key={reason}
              type="button"
              style={s.whyBtn}
              disabled={busy}
              onClick={() => { setPicking(false); reply.decline(task, reason); }}
            >
              {reason}
            </button>
          ))}
          <button type="button" style={s.whyCancel} disabled={busy} onClick={() => setPicking(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <div style={s.replies}>
          <button
            type="button"
            style={s.gotIt}
            disabled={busy}
            onClick={() => reply.confirm(task)}
          >
            Got it
          </button>
          {/* Quieter than "Got it", and never red. Refusing is an ordinary,
              correct answer — a directive can land mid-induction — so dressing
              it as an error spends alarm colour that must stay finite. */}
          <button
            type="button"
            style={s.cantNow}
            disabled={busy}
            onClick={() => setPicking(true)}
          >
            Can't right now
          </button>
        </div>
      )}

      {failed ? (
        <p style={s.retry} role="status">Not recorded yet — tap again.</p>
      ) : null}
    </li>
  );
}

export default function DirectivesCard() {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const dateKey = useDayKey();
  const { tasks } = useTasks(user?.activeFacilityId, dateKey);
  const reply = useDirectiveReply(uid);

  const mine = uid ? tasksForRecipient(tasks || [], uid) : [];
  // ⚠️ NOTHING AT ALL WHEN THERE IS NOTHING. See the note at the top: this sits
  // on a boot path, and an empty card there is noise every single shift.
  if (!mine.length) return null;

  return (
    <section style={s.card} aria-label="What the board has asked of you">
      <h2 style={s.title}>Asked of you</h2>
      <ul style={s.list}>
        {mine.map((task) => (
          <DirectiveRow key={task.id} task={task} uid={uid} reply={reply} />
        ))}
      </ul>
    </section>
  );
}

const s = {
  card: {
    background: lobby.plaque,
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.lg,
    padding: space.md,
    marginBottom: space.md,
  },
  title: {
    margin: `0 0 ${space.sm}px`,
    fontSize: 12,
    fontWeight: font.weight.bold,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: lobby.brassInk,
  },
  list: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: space.sm },
  row: {
    borderTop: `1px solid ${lobby.sand}`,
    paddingTop: space.sm,
  },
  meta: { display: "flex", gap: space.sm, alignItems: "baseline", flexWrap: "wrap" },
  from: { fontSize: 12, fontWeight: font.weight.bold, color: color.text.primary },
  room: { fontSize: 12, color: lobby.brassInk, fontWeight: font.weight.bold },
  time: { fontSize: 11, color: color.text.muted },
  // The instruction is the largest type here — the card's entire job is to say
  // what was asked.
  text: { margin: `4px 0 ${space.sm}px`, fontSize: 15, lineHeight: 1.4, color: color.text.primary },
  replies: { display: "flex", gap: space.sm, flexWrap: "wrap" },
  // 44px targets: these write.
  gotIt: {
    minHeight: 44,
    minWidth: 96,
    padding: "8px 16px",
    border: "none",
    borderRadius: radius.md,
    background: color.accent.primary,
    color: "#fff",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: font.weight.bold,
    cursor: "pointer",
  },
  cantNow: {
    minHeight: 44,
    minWidth: 96,
    padding: "8px 16px",
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.md,
    background: "transparent",
    color: color.text.secondary,
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: font.weight.bold,
    cursor: "pointer",
  },
  why: { display: "flex", flexDirection: "column", gap: 6, marginTop: space.sm },
  whyTitle: {
    margin: 0,
    fontSize: 11,
    fontWeight: font.weight.bold,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: color.text.secondary,
  },
  whyBtn: {
    minHeight: 44,
    padding: "8px 12px",
    border: `1px solid ${lobby.sand}`,
    borderRadius: radius.md,
    background: "transparent",
    color: color.text.primary,
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: font.weight.bold,
    textAlign: "left",
    cursor: "pointer",
  },
  whyCancel: {
    minHeight: 44,
    padding: "8px 12px",
    border: "none",
    borderRadius: radius.md,
    background: "transparent",
    color: color.text.secondary,
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: font.weight.bold,
    cursor: "pointer",
  },
  seen: { margin: 0, fontSize: 13, color: color.status.active.text, fontWeight: font.weight.bold },
  // Amber, never red — see the note on the button above.
  declined: { margin: 0, fontSize: 13, color: color.status.closing.text, fontWeight: font.weight.bold },
  retry: { margin: `${space.sm}px 0 0`, fontSize: 12, color: color.text.muted },
};
